import React from 'react';
import { RouterProvider } from 'react-router-dom';
import './App.css';
import { QueryClient, QueryClientProvider } from 'react-query';
import { SnackbarProvider as NotistackProvider } from 'notistack';
import { CssBaseline } from '@mui/material';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { BRAND, META_TAGS } from './constants/branding';

// Initialize i18n
import './i18n';

// Providers
import { ThemeProvider } from './contexts/ThemeContext';
import { AppearanceProvider } from './contexts/AppearanceContext';
import { AvatarVersionProvider } from './contexts/AvatarVersionContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { GoogleOAuthProvider } from '@react-oauth/google';

// Router
import { router } from './routes';

// Error handling
import ErrorBoundary from './components/common/ErrorBoundary';
import NotificationProvider from './components/common/NotificationSystem';
import { useNetworkStatus } from './hooks/useNetworkStatus';


// Authentication migration (runs automatically on import)
import './utils/authMigration';

// Debug utilities (development only)
// Note: debugAuth utility removed - use browser DevTools or DebugAuth page instead

// Network status indicator component
const NetworkStatusIndicator: React.FC = () => {
  const networkStatus = useNetworkStatus();
  const { t } = useTranslation();
  
  if (networkStatus.isOnline) {
    return null;
  }
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      backgroundColor: '#f44336',
      color: 'white',
      padding: '8px 16px',
      textAlign: 'center',
      fontSize: '14px',
      zIndex: 10000,
      fontFamily: 'Roboto, sans-serif'
    }}>
      ⚠️ {t('common:networkOffline', 'You\'re currently offline. Some features may not be available.')}
    </div>
  );
};

// Create a client for React Query with enhanced error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error instanceof Error && 'status' in error) {
          const status = (error as any).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
    mutations: {
      retry: (failureCount, error) => {
        // Don't retry mutations on client errors
        if (error instanceof Error && 'status' in error) {
          const status = (error as any).status;
          if (status >= 400 && status < 500) {
            return false;
          }
        }
        return failureCount < 2; // Retry mutations only once
      }
    }
  },
});

function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

  return (
    <HelmetProvider>
      {/* Default SEO tags — overridden per-page by <SEO> component */}
      <Helmet
        defaultTitle={META_TAGS.title}
        titleTemplate={META_TAGS.titleTemplate}
      >
        <meta name="description" content={META_TAGS.description} />
        <meta name="keywords" content={META_TAGS.keywords} />
        <meta property="og:site_name" content={META_TAGS.og.siteName} />
        <meta property="og:type" content={META_TAGS.og.type} />
        <meta property="og:locale" content={META_TAGS.og.locale} />
        <meta property="og:image" content={`${BRAND.website}/logo512.png`} />
        <meta name="twitter:card" content={META_TAGS.twitter.card} />
        <meta name="twitter:site" content={META_TAGS.twitter.site} />
      </Helmet>
      <GoogleOAuthProvider clientId={googleClientId}>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary 
          onError={(error, errorInfo) => {
            // Log error to monitoring service in production
            if (process.env.NODE_ENV === 'production') {
              console.error('App Error Boundary:', error, errorInfo);
              // Send to error monitoring service
              // errorMonitoringService.captureException(error, { extra: errorInfo });
            }
          }}
        >
          <NotificationProvider maxNotifications={5}>
            <LanguageProvider defaultLanguage="en">
              <ThemeProvider>
                <AppearanceProvider>
                  <AvatarVersionProvider>
                  <NotistackProvider 
                    maxSnack={3} 
                    autoHideDuration={3000}
                    anchorOrigin={{ 
                      vertical: 'bottom', 
                      horizontal: 'left' 
                    }}
                  >
                    <SnackbarProvider>
                      <CssBaseline />
                      <NetworkStatusIndicator />
                      <RouterProvider router={router} />
                    </SnackbarProvider>
                  </NotistackProvider>
                  </AvatarVersionProvider>
                </AppearanceProvider>
              </ThemeProvider>
            </LanguageProvider>
          </NotificationProvider>
        </ErrorBoundary>
      </QueryClientProvider>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App;
